import React, { useState, useEffect, useMemo } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import AppFooter from "@/components/AppFooter";
import axios from "axios";
import {
  Search, BookOpen, FlaskConical, Heart, Brain, Activity,
  ShieldCheck, Loader2, Filter, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_META = {
  insulin_indices: { label: "Insulin Indices", labelBg: "Инсулинови индекси", color: "#6366F1", icon: Activity },
  diabetes: { label: "Diabetes", labelBg: "Диабет", color: "#EF4444", icon: Heart },
  cancer: { label: "Cancer", labelBg: "Онкология", color: "#EC4899", icon: ShieldCheck },
  obesity: { label: "Obesity", labelBg: "Затлъстяване", color: "#F59E0B", icon: Activity },
  metabolic_syndrome: { label: "Metabolic Syndrome", labelBg: "Метаболитен синдром", color: "#0EA5E9", icon: FlaskConical },
  liver: { label: "Liver / NAFLD", labelBg: "Черен дроб / НМЧБ", color: "#10B981", icon: FlaskConical },
  cardiovascular: { label: "Cardiovascular", labelBg: "Сърдечно-съдови", color: "#EF4444", icon: Heart },
  glycemic: { label: "Glycemic Index", labelBg: "Гликемичен индекс", color: "#8B5CF6", icon: Brain },
  inflammation: { label: "Inflammation", labelBg: "Възпаление", color: "#F97316", icon: FlaskConical },
  methodology: { label: "Methodology", labelBg: "Методология", color: "#64748B", icon: BookOpen },
  general: { label: "General", labelBg: "Общи", color: "#94A3B8", icon: BookOpen },
};

export default function ScienceLibrary() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const [artRes, catRes] = await Promise.all([
          axios.get(`${API}/science/articles`),
          axios.get(`${API}/science/categories`),
        ]);
        setArticles(artRes.data.articles || []);
        setCategories(catRes.data.categories || {});
      } catch (err) { if (process.env.NODE_ENV === 'development') console.error("Failed to load science articles:", err); }
      finally { setLoading(false); }
    })();
  }, [API]);

  const filtered = useMemo(() => {
    let list = articles;
    if (activeCat !== "all") {
      list = list.filter(a => a.categories?.includes(activeCat));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.title?.toLowerCase().includes(q) || a.author?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [articles, activeCat, search]);

  const sortedCats = useMemo(() =>
    Object.entries(categories).sort((a, b) => b[1] - a[1]),
  [categories]);

  return (
    <div className={`min-h-screen ${isDark ? "bg-gray-950" : "bg-[#FAFBFC]"}`}>
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#6366F1]/10 text-[#6366F1] text-xs font-semibold mb-4">
            <FlaskConical className="w-3.5 h-3.5" />
            {bg ? "РЕЦЕНЗИРАНА НАУКА" : "PEER-REVIEWED SCIENCE"}
          </div>
          <h1 className={`text-2xl sm:text-3xl font-bold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Научна библиотека" : "Scientific Library"}
          </h1>
          <p className={`text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-gray-400" : "text-[#64748B]"}`}>
            {bg
              ? "Научни публикации с DOI линкове за инсулинови индекси, метаболитен синдром, диабет, онкология и повече."
              : "Scientific publications with DOI links on insulin indices, metabolic syndrome, diabetes, oncology and more."}
          </p>
        </div>

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={bg ? "Търси по автор или заглавие..." : "Search by author or title..."}
              data-testid="science-search"
              className={`pl-10 h-10 rounded-xl ${isDark ? "bg-gray-900 border-gray-800 text-white" : "bg-white border-gray-200"}`} />
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Filter className="w-3.5 h-3.5 text-gray-400" />
            <span className={`${isDark ? "text-gray-500" : "text-gray-400"}`}>{filtered.length} / {articles.length}</span>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="science-categories">
          <button onClick={() => setActiveCat("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeCat === "all"
                ? (isDark ? "bg-white text-gray-900" : "bg-[#1A1A2E] text-white")
                : (isDark ? "bg-gray-800 text-gray-400 hover:text-white" : "bg-gray-100 text-gray-500 hover:text-gray-700")
            }`} data-testid="science-cat-all">
            {bg ? "Всички" : "All"} ({articles.length})
          </button>
          {sortedCats.map(([cat, count]) => {
            const meta = CAT_META[cat] || CAT_META.general;
            return (
              <button key={cat} onClick={() => setActiveCat(cat)}
                data-testid={`science-cat-${cat}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activeCat === cat
                    ? "text-white shadow-sm"
                    : (isDark ? "bg-gray-800 text-gray-400 hover:text-white" : "bg-gray-100 text-gray-500 hover:text-gray-700")
                }`}
                style={activeCat === cat ? { backgroundColor: meta.color } : {}}>
                {bg ? meta.labelBg : meta.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Articles List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className={`w-12 h-12 mx-auto mb-3 ${isDark ? "text-gray-700" : "text-gray-300"}`} />
            <p className={isDark ? "text-gray-500" : "text-gray-400"}>
              {bg ? "Няма намерени публикации" : "No articles found"}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="science-articles-list">
            {filtered.map((article) => (
              <Card key={article.id} data-testid={`science-article-${article.id}`}
                className={`transition-all hover:shadow-md ${isDark ? "bg-gray-900 border-gray-800 hover:border-gray-700" : "border-gray-100 hover:border-gray-200"}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? "bg-[#6366F1]/15" : "bg-[#6366F1]/8"}`}>
                      <BookOpen className="w-4 h-4 text-[#6366F1]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className={`text-sm font-semibold leading-snug mb-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                        {article.title}
                      </h3>
                      <div className={`flex items-center gap-3 text-xs mb-2.5 ${isDark ? "text-gray-400" : "text-[#64748B]"}`}>
                        {article.author && <span className="font-medium">{article.author}</span>}
                        {article.doi_url && (
                          <a href={article.doi_url} target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`doi-link-${article.id}`}
                            className="inline-flex items-center gap-1 text-[#6366F1] hover:text-[#4F46E5] font-medium transition-colors">
                            <ExternalLink className="w-3 h-3" />
                            DOI
                          </a>
                        )}
                        {article.pubmed_url && (
                          <a href={article.pubmed_url} target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`pubmed-link-${article.id}`}
                            className="inline-flex items-center gap-1 text-[#10B981] hover:text-[#059669] font-medium transition-colors">
                            <ExternalLink className="w-3 h-3" />
                            PubMed
                          </a>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {article.categories?.map(cat => {
                          const meta = CAT_META[cat] || CAT_META.general;
                          return (
                            <Badge key={cat} variant="outline"
                              className="text-[10px] px-2 py-0 border-opacity-40"
                              style={{ color: meta.color, borderColor: meta.color }}>
                              {bg ? meta.labelBg : meta.label}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <AppFooter />
    </div>
  );
}
